import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { useLang } from "../../contexts/LanguageContext";

function Counter({ to, suffix = "", duration = 1.6 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    let raf = 0;
    const step = (now) => {
      const p = Math.min(1, (now - start) / (duration * 1000));
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(eased * to));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);

  return (
    <span ref={ref} className="tabular-nums">
      {val}
      <span className="text-[#1E5BD6]">{suffix}</span>
    </span>
  );
}

export default function Stats() {
  const { t } = useLang();
  return (
    <section
      id="stats"
      data-testid="stats-section"
      className="relative py-20 md:py-28"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase font-bold text-[#1E5BD6]">
              <span className="h-px w-8 bg-[#1E5BD6]" />
              {t.stats.label}
            </div>
            <h2
              data-testid="stats-title"
              className="mt-5 font-heading font-bold text-slate-900 text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1]"
            >
              {t.stats.title}
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 border-t border-slate-200">
          {t.stats.items.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              data-testid={`stat-${i}`}
              className={`py-8 lg:py-10 px-2 ${i !== 0 ? "lg:border-l border-slate-200" : ""} ${
                i % 2 !== 0 ? "border-l border-slate-200 lg:border-l" : ""
              }`}
            >
              <div className="font-heading font-extrabold text-slate-900 text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-none">
                <Counter to={item.value} suffix={item.suffix} />
              </div>
              <div className="mt-4 text-xs sm:text-sm font-semibold tracking-wider uppercase text-slate-500">
                {item.label}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
